import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Cacheable } from 'ngx-cacheable';
import {
  CarrierOffer,
  CarrierOfferDC,
  CarrierOfferPayload
} from './CarrierOfferDC';
import { AuthService } from '../../authentication/_services/auth.service';
import { UserDetails } from './UserDetails';
import { CarrierSummaryDC } from '../../carrier/dto/CarrierSummaryDC';
import { SearchService } from '../../carrier-search/services/search.service';
import { ActiveShipmentDC } from '../../carrier/dto/ActiveShipmentDC';
import { Store } from '@ngrx/store';
import { AppState } from '../../appstate.model';
import { Configuration } from '../configuration/Configuration';
import { GetShipmentByPrimaryReferenceDC } from './GetShipmentByPrimaryReferenceDC';
import { CurrencyService } from '../../Services/currency.service';
import { TenderValidationResponse } from './TenderValidationResponse';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { FEATURE_FLAGS } from '../feature-flag/feature-flag.service';

type OfferPrerequisites = {
  userDetails: UserDetails;
  rate?: number;
};

@Injectable()
export class CarrierOfferService {
  config: Configuration;
  flags: LDFlagSet;
  
  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private searchServie: SearchService,
    private store: Store<AppState>,
    private currencyService: CurrencyService
  ) {
    // TODO: Fix store subscription leaks
    this.store
      .select((x) => x.Configuration)
      .subscribe((x) => {
        this.config = x;
      });

    this.store
      .select((x) => x.FeatureFlags)
      .subscribe((ld) => {
        this.flags = ld.flags;

        if (
          this.flags[FEATURE_FLAGS.useV3CarrierOffers] !== undefined &&
          this.flags[FEATURE_FLAGS.useV3CarrierOffers].current !== undefined
        ) {
          this.flags[FEATURE_FLAGS.useV3CarrierOffers] =
            this.flags[FEATURE_FLAGS.useV3CarrierOffers].current;
        }
      });
  }

  private createHeaders(userDetails: UserDetails): HttpHeaders {
    return new HttpHeaders({
      UserID: userDetails.userID.toString(),
      UserName: userDetails.userName,
      EnterpriseID: userDetails.enterpriseID.toString()
    });
  }

  async getOfferPrerequisites(
    effectiveDate: string,
    currencyCode: string
  ): Promise<OfferPrerequisites> {
    const userDetails = await this.getUserDetails().toPromise();

    if (this.flags[FEATURE_FLAGS.useV3CarrierOffers]) {
      return { userDetails };
    } else {
      const rate = await this.getRate(effectiveDate, currencyCode);
      return {
        userDetails,
        rate
      };
    }
  }

  submitOffer(
    shipmentID: number,
    userDetails: UserDetails,
    offer: CarrierOfferPayload
  ): Observable<any> {
    if (this.flags[FEATURE_FLAGS.useV3CarrierOffers]) {
      return this.createPendingQuote(shipmentID, userDetails, offer.v3Offer);
    } else {
      return this.createOffer(shipmentID, userDetails, offer.v1Offer);
    }
  }

  private createPendingQuote(
    shipmentID: number,
    userDetails: UserDetails,
    offer: CarrierOffer
  ): Observable<any> {
    const headers = this.createHeaders(userDetails);
    const createOfferPatch = [
      {
        targetType: 'ManualOffer',
        patchCommands: [{ op: 'add', path: '/', value: offer }]
      }
    ];
    return this.http.patch<any>(
      `${this.config.shipmentApiUrl}/v3/shipment/${shipmentID}/pending-quote`,
      createOfferPatch,
      { headers }
    );
  }

  private createOffer(
    shipmentId: number,
    userDetails: UserDetails,
    offer: CarrierOfferDC
  ): Observable<any> {
    const headers = this.createHeaders(userDetails);
    return this.http.post<any>(
      `${this.config.shipmentServiceUrl}shipment/${shipmentId}/rate`,
      offer,
      { headers }
    );
  }

  findShipment(
    bgNumber: string,
    userDetails: UserDetails
  ): Observable<GetShipmentByPrimaryReferenceDC> {
    const headers = new HttpHeaders({
      UserID: userDetails.userID.toString(),
      UserName: userDetails.userName,
      EnterpriseID: userDetails.enterpriseID.toString()
    });

    return this.http.get<GetShipmentByPrimaryReferenceDC>(
      `${this.config.shipmentServiceUrl}shipment/getByPrimaryReference/${bgNumber}`,
      {
        headers: headers
      }
    );
  }

  @Cacheable()
  getUserDetails(): Observable<UserDetails> {
    const headers = new HttpHeaders({
      UserName: this.authService.loggedAppUser.appUserName
    });
    return this.http.get<UserDetails>(
      `${this.config.shipmentServiceUrl}user/current`,
      { headers: headers }
    );
  }

  @Cacheable()
  getCarrierSummary(carrierCode: string): Observable<CarrierSummaryDC> {
    return this.http.get<CarrierSummaryDC>(
      `${this.config.carrierServiceUrl}${carrierCode}/summary`
    );
  }

  @Cacheable()
  getActiveShipment(shipmentId: number): Observable<ActiveShipmentDC> {
    return this.searchServie.getActiveShipment(shipmentId);
  }

  getCustomerCurrencyCode(
    shipmentId: number,
    userDetails: UserDetails
  ): Promise<string> {
    const headers = new HttpHeaders({
      UserID: userDetails.userID.toString(),
      UserName: userDetails.userName,
      EnterpriseID: userDetails.enterpriseID.toString()
    });
    // Hack to circumvent expectations of v1 url
    const shipmentV2Url = this.config.shipmentServiceUrl.replace('v1', 'v2');
    return this.http
      .get<any>(`${shipmentV2Url}${shipmentId}/ShipmentRate/target`, {
        headers: headers
      })
      .toPromise()
      .then((x) => {
        return x.targetRate.currencyCode;
      });
  }

  async getRate(effectiveDate: string, currencyCode: string): Promise<number> {
    if (currencyCode === 'USD') {
      return Promise.resolve(1);
    }
    const countryCode = this.toCountryCode(currencyCode);
    const results = await (
      await this.currencyService.getCurrency(effectiveDate)
    ).toPromise();
    const selectedRate = results.find((x) => x.country === countryCode);
    return selectedRate.value || 1;
  }

  validateManualOffer({
    shipmentId,
    carrierId = 0,
    fumCarrierId = 0
  }: {
    shipmentId: number;
    carrierId?: number;
    fumCarrierId?: number;
  }): Promise<TenderValidationResponse> {
    var params = {
      shipmentId: shipmentId.toString(),
      ...(carrierId > 0 ? { carrierId: carrierId.toString() } : {}),
      ...(fumCarrierId > 0 ? { fumCarrierId: fumCarrierId.toString() } : {})
    };
    return this.http
      .get<TenderValidationResponse>(
        `${this.config.carrierServiceUrl}Tender/validate-offer`,
        { params }
      )
      .toPromise();
  }

  private toCountryCode(currencyCode: string): string {
    const currencyCodeMap = {
      USD: 'USA',
      CAD: 'CAN'
    };
    return currencyCodeMap[currencyCode.toUpperCase()];
  }
}
